import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Map from "./Map.js";
import CourtNotFound from "./CourtNotFound.js";
import CourtAddress from "./AddressFormatter.js";
import ContactInfo from "./details/contact.js";
import VenueCourtDetails from "./details/courts.js";
import Verified from "./details/verified.js";
import Facilities from "./details/facilities.js";
import InfoIcon from "./details/infoicon.js";
import "./CourtDetailNew.css";

function CourtDetailNew() {
  const { slug } = useParams();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const fetchVenue = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_FUTSAL_DIRECTORY_API_URL}/api/venues/slug/${slug}`);
        if (!res.ok) throw new Error("Venue not found");
        const data = await res.json();

        setVenue(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        setVenue(null);
      } finally {
        setLoading(false);
      }
    };
    fetchVenue();
  }, [slug]);

  const nearbyCourts = useMemo(() => {
    if (!venue) return [];

    const haversineDistance = (lat1, lon1, lat2, lon2) => {
      const R = 6371;
      const toRad = (angle) => (angle * Math.PI) / 180;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    };

    return venue.sharedCourts?.filter(c => c.slug !== venue.slug)?.map((c) => {
      const lat1 = venue.latitude;
      const lon1 = venue.longitude;
      const lat2 = c.latitude ?? 0;
      const lon2 = c.longitude ?? 0;

      return {
        ...c,
        distance: haversineDistance(lat1, lon1, lat2, lon2).toFixed(2),
        address: {
          country: c.country || "",
          state: c.state || "",
          city: c.city || ""
        },
        photos: c.photos || []
      };
    }) || [];
  }, [venue]);

  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prevIndex) =>
      (prevIndex + 1) % (venue?.photos?.length || 1)
    );
  };

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prevIndex) =>
      (prevIndex - 1 + (venue?.photos?.length || 1)) % (venue?.photos?.length || 1)
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error || !venue) return <CourtNotFound />;

  console.log("👟 Courts data passed to <VenueCourtDetails />:", venue.courts);
  console.log("👟 Venue:", venue);


  return (
    <div className="court-detail-container">
      <div className="court-detail-horizontal-container">
        <div className="court-gallery">
          <button className="nav-button left" onClick={handlePrevPhoto}>◀</button>
          <img
            src={venue.images?.length > 0
              ? venue.images[currentPhotoIndex]?.url || "/images/futsal-ai.png"
              : "/images/futsal-ai.png"
            }
            alt={venue.name}
          />
          <button className="nav-button right" onClick={handleNextPhoto}>▶</button>
        </div>

        <div className="court-detail-info">
          <h1 style={{ marginTop: 0, marginBottom: 0 }}>{venue.name}</h1>
          <p>{venue.description}</p>

          <CourtAddress
            court={{
              ...venue,
              address: {
                firstLine: venue.address_line1,
                secondLine: venue.address_line2,
                city: venue.city,
                state: venue.state,
                postcode: venue.postcode,
                country: venue.country,
              },
            }}
          />

          <Facilities court={venue} />
          <ContactInfo contact={venue} />
          <Verified contact={venue.is_verified} />
        </div>
      </div>

      <div className="court-detail-horizontal-container-2">
        <div className="court-detail-info">
          <div className="court-detail-info-header">
            <h1 style={{ marginTop: 0, marginBottom: 0 }}>Court Information:</h1>
            <InfoIcon
              content={
                <div>
                  <p>We strive to provide the most up-to-date court info.</p>
                  <p>If something doesn't look right, email us at:</p>
                  <p>thefutsaldirectory@gmail.com</p>
                </div>
              }
            />
          </div>
          <VenueCourtDetails courts={venue.courts} />
        </div>
      </div>

      <Map courts={[{
        ...venue,
        coordinates: { lat: venue.latitude, lng: venue.longitude }
      }]} />

      {nearbyCourts.length > 0 && (
        <div className="nearby-courts">
          <h2>Nearby Courts</h2>
          <div className="nearby-courts-gallery">
            {nearbyCourts.map((c, index) => {
              const country = c.address?.country?.toLowerCase().replace(/\s+/g, '-') || "unknown";
              const state = c.address?.state?.toLowerCase().replace(/\s+/g, '-') || "unknown";
              const city = c.address?.city?.toLowerCase().replace(/\s+/g, '-') || "unknown";

              return (
                <div
                  key={index}
                  className="nearby-court-item"
                  onClick={() => navigate(`/courts/${country}/${state}/${city}/${c.slug}`)}
                >
                  <img
                    src={c.photos?.[0] || "/images/futsal-ai.png"}
                    alt={c.name}
                    className="nearby-court-photo"
                  />
                  <p>
                    {c.name} ({c.distance} km away)
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default CourtDetailNew;
